import { ActionIcon, Button, Checkbox, Group, Stack, TextInput, Title } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { PencilLine } from "lucide-react";
import { useState, type FormEvent } from "react";

import {
  getGetMeQueryKey,
  useGetMe,
  useSetMyConsents,
} from "../../api/generated/endpoints/identity/identity";
import {
  getListMyTeamsQueryKey,
  useListMyTeams,
  useRenameTeam,
} from "../../api/generated/endpoints/teams/teams";
import { TeamUpdate } from "../../api/generated/zod";
import { ErrorAlert } from "../ErrorAlert";

const NAME_PROBLEM = "Give the team a name of up to 100 characters.";

/**
 * The team's name as the Team tab's title (18px/700, design-system.md 3.7), with a pencil for
 * those who may rename it. The name comes from the teams list the header already reads, so a
 * rename shows in the team select too once the list is read again.
 */
export function TeamNameHeader({ teamId, canRename }: { teamId: string; canRename: boolean }) {
  const queryClient = useQueryClient();
  const name = useListMyTeams({
    query: {
      select: (teams) => teams.find((team) => team.id.toLowerCase() === teamId.toLowerCase())?.name,
    },
  }).data;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [invalid, setInvalid] = useState(false);
  const rename = useRenameTeam({
    mutation: {
      onSuccess: async () => {
        setEditing(false);
        await queryClient.invalidateQueries({ queryKey: getListMyTeamsQueryKey() });
      },
    },
  });
  const start = () => {
    rename.reset();
    setDraft(name ?? "");
    setInvalid(false);
    setEditing(true);
  };
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsed = TeamUpdate.safeParse({ name: draft.trim() });
    setInvalid(!parsed.success);
    if (parsed.success) rename.mutate({ teamId, data: parsed.data });
  };
  if (!editing) {
    return (
      <Group gap={8} wrap="nowrap">
        <Title order={1} fz={18} fw={700}>
          {name ?? "Team"}
        </Title>
        {canRename ? (
          <ActionIcon
            variant="subtle"
            color="gray"
            aria-label="Edit team name"
            c="var(--cai-color-icon-muted)"
            onClick={start}
          >
            <PencilLine size={16} />
          </ActionIcon>
        ) : null}
      </Group>
    );
  }
  return (
    <form onSubmit={submit} noValidate>
      <Stack gap="xs">
        <Group gap={8} align="flex-start" wrap="nowrap">
          <TextInput
            aria-label="Team name"
            placeholder="Enter team name"
            size="sm"
            w={235}
            value={draft}
            disabled={rename.isPending}
            onChange={(event) => setDraft(event.currentTarget.value)}
            error={invalid ? NAME_PROBLEM : undefined}
          />
          <Button type="submit" size="sm" loading={rename.isPending}>
            Save
          </Button>
          <Button
            type="button"
            size="sm"
            variant="default"
            disabled={rename.isPending}
            onClick={() => setEditing(false)}
          >
            Cancel
          </Button>
        </Group>
        {rename.isError ? <ErrorAlert error={rename.error} /> : null}
      </Stack>
    </form>
  );
}

/**
 * The live app's "Send me product updates and marketing emails" beside the team name. It is the
 * user's own consent (`PUT /v1/me/consents`), shown here because that is where the live app has
 * it; the ads consent is sent back unchanged.
 */
export function MarketingConsent() {
  const queryClient = useQueryClient();
  const me = useGetMe();
  const consent = useSetMyConsents({
    mutation: { onSuccess: () => queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() }) },
  });
  if (me.data === undefined) return null;
  const { marketing_at, ads_at } = me.data.consents;
  return (
    <Stack gap="xs">
      <Checkbox
        label="Send me product updates and marketing emails"
        checked={marketing_at !== null}
        disabled={consent.isPending}
        onChange={(event) =>
          consent.mutate({ data: { marketing: event.currentTarget.checked, ads: ads_at !== null } })
        }
      />
      {consent.isError ? <ErrorAlert error={consent.error} /> : null}
    </Stack>
  );
}
